import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import PreformatifDiagnosticModal, {
  PreformatifDiagnosticModalMethods,
} from "@/components/molecules/modals/company/courses/PreformatifDiagnosticModal";
import CompanyCourse from "@/interfaces/CompanyCourse";
import {
  useGetCompanyCoursesQuery,
} from "@/services/companies/courses";
import { useGetPreformatifDiagnosticQuery } from "@/services/companies/courses/preformatifDiagnostic";
import {
  faFileLines,
  faPen,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useRef } from "react";
import { Button, Card, ListGroup } from "react-bootstrap";

interface Props {
  companyId: string;
}

const DiagnosticRow: FC<{
  companyId: string;
  course: CompanyCourse;
  onOpen: (courseId: string) => void;
}> = ({ companyId, course, onOpen }) => {
  const { data, isLoading } = useGetPreformatifDiagnosticQuery([
    companyId,
    course.id,
  ]);

  const title = course.course?.name || "Formation sans intitulé";
  const status = data?.status;

  return (
    <ListGroup.Item className="d-flex align-items-center justify-content-between gap-3 px-0">
      <div className="d-flex align-items-center gap-3 min-w-0">
        <div className="rounded-circle bg-200 p-2 text-primary">
          <FontAwesomeIcon icon={faFileLines} />
        </div>
        <div className="min-w-0">
          <div className="fw-semi-bold text-truncate">{title}</div>
          <small className="text-600">
            {course.hoursNumber ?? course.course?.hours ?? 0} heure(s)
          </small>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2 flex-shrink-0">
        {isLoading ? (
          <small className="text-600">Chargement…</small>
        ) : (
          <SoftBadge bg={status === "finalized" ? "success" : status === "draft" ? "warning" : "secondary"}>
            {status === "finalized"
              ? "Finalisé"
              : status === "draft"
                ? "Brouillon"
                : "Non créé"}
          </SoftBadge>
        )}
        <Button
          variant="falcon-default"
          size="sm"
          onClick={() => onOpen(course.id)}
          aria-label={`${status ? "Modifier" : "Créer"} le diagnostic de ${title}`}
        >
          <FontAwesomeIcon icon={status ? faPen : faPlus} className="me-1" />
          {status ? "Ouvrir" : "Créer"}
        </Button>
      </div>
    </ListGroup.Item>
  );
};

const CompanyDiagnosticsCard: FC<Props> = ({ companyId }) => {
  const modalRef = useRef<PreformatifDiagnosticModalMethods>(null);
  const { data, isLoading, isError, error } = useGetCompanyCoursesQuery([
    companyId,
    { pageIndex: 0, pageSize: 100 },
  ]);

  if (isLoading) return <Loader />;
  if (isError) return <ErrorComponent error={error} />;

  const courses = data?.data || [];

  return (
    <>
      <Card className="shadow-none border">
        <Card.Header className="d-flex align-items-center justify-content-between">
          <div>
            <h5 className="mb-1">Diagnostics préformatifs</h5>
            <p className="mb-0 text-600">
              Préparez et retrouvez les diagnostics liés aux formations de cette entreprise.
            </p>
          </div>
          <SoftBadge bg="primary">{courses.length}</SoftBadge>
        </Card.Header>
        <Card.Body className="pt-0">
          {courses.length === 0 ? (
            <div className="text-center text-600 py-4">
              Aucune formation n’est encore associée à cette entreprise.
            </div>
          ) : (
            <ListGroup variant="flush">
              {courses.map((course) => (
                <DiagnosticRow
                  key={course.id}
                  companyId={companyId}
                  course={course}
                  onOpen={(courseId) => modalRef.current?.showOrHiddenModal(courseId)}
                />
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>
      <PreformatifDiagnosticModal ref={modalRef} companyId={companyId} />
    </>
  );
};

export default CompanyDiagnosticsCard;
